// HIJACKED FOR JOB 20: This file no longer processes.
// It just scrapes data and calls the AI backend.

declare const XLSX: any

import { httpsCallable } from 'firebase/functions'
import { functions } from '@/core/firebase'
import { getPeriodFromFile } from './utils' // We'll still use this for a period *guess*

// Define the types for the AI functions
const aiCategorizePnl = httpsCallable(functions, 'aiCategorizePnlData')
export const saveConfirmedPnl = httpsCallable(functions, 'saveConfirmedPnlData')

interface ScrapedRow {
  item: string
  value: number
}

// In: apps/web/src/uploads/pnl.ts
// Replace the entire getAiPnlSuggestions function:

export async function getAiPnlSuggestions(file: File) {
  const data = await file.arrayBuffer()
  const workbook = XLSX.read(data)
  
  const sheetName = workbook.SheetNames.includes('P&L Data') ? 'P&L Data' : workbook.SheetNames[0]
  const worksheet = workbook.Sheets[sheetName]
  if (!worksheet) {
    throw new Error('Could not find any sheets in the Excel file.')
  }

  let periodGuess: string | null = null
  try {
    periodGuess = getPeriodFromFile(worksheet)
  } catch (e) {
    console.warn('Could not find period in B2, will ask AI to find it.')
  }

  const scrapedRows: ScrapedRow[] = []
  
  // Convert sheet to JSON, header: 1 makes it an array of arrays
  const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

  // Loop through all rows to find item/value pairs
  for (const row of jsonData) {
    if (!Array.isArray(row) || row.length < 2) continue

    // --- [THIS IS THE FIX] ---
    // We make the scraper smarter.
    // We assume the *Item* is in Column B and *Value* is in Column C.
    // If Column B is empty, we fall back to Column A.

    const colA = row[0] // Main Category (e.g., "Beban Operasional (OPEX)")
    const colB = row[1] // Sub-Category / Item (e.g., "Biaya Gaji")
    const colC = row[2] // Amount (e.g., 100000)

    let item: string | null = null
    let value: number | null = null

    // Prioritize Column B (Sub-Category) as the item name
    if (typeof colB === 'string' && colB.trim().length > 2) {
      item = colB.trim()
    } 
    // Fallback to Column A if B is blank
    else if (typeof colA === 'string' && colA.trim().length > 2) {
      item = colA.trim()
    }
    
    // Find the value (must be a number)
    if (typeof colC === 'number' && colC !== 0) {
      value = colC
    } 
    // Fallback: sometimes the value is in Column B (e.g., "Period:", "01/12/2024")
    // This is a bad row, but we check it anyway.
    else if (typeof colB === 'number' && colB !== 0) {
      value = colB
    }
    // --- [END FIX] ---


    // If we found a valid pair...
    if (item && value) {
      // ...and it's not a "total" row...
      const lowerItem = item.toLowerCase()
      if (!lowerItem.includes('total') && !lowerItem.includes('sub-total')) {
        scrapedRows.push({ item, value })
      }
    }
  }

  if (scrapedRows.length === 0) {
    throw new Error('Could not find any item/value rows in the file. The file might be empty or in an unsupported format.')
  }

  // Send the clean list to the AI
  const result = await aiCategorizePnl({
    scrapedRows: scrapedRows,
    fileName: file.name,
    periodGuess: periodGuess 
  })

  return result.data as {
    branchName: string,
    period: string,
    suggestions: {
      item: string,
      value: number,
      suggestedCategory: string
    }[]
  }
}

// Keep the download template function as it's still useful
export function downloadPnlTemplate() {
  const instructions = [
    { Step: 1, Instruction: 'In the \'P&L Data\' sheet, enter your Business Name in cell B1 and a date from the desired month in cell B2 (e.g., \'01/12/2024\' for December 2024).' },
    { Step: 2, Instruction: 'For the \'Main Category\' column, you MUST use the exact values from the list provided.' },
    { Step: 3, Instruction: 'Under \'Beban Operasional (OPEX)\', you MUST include \'Wages\' and \'Rent\'. Under \'Beban Non Operasional\', you MUST include \'Advertising\'.' },
  ]
  const pnlSheetData = [
    { A: 'Business Name:', B: '[Enter Business Name Here]' },
    { A: 'Period:', B: '01/12/2024' },
    {}, // Empty row for spacing
    { A: 'Main Category', B: 'Sub-Category', C: 'Amount' },
    { A: 'Pendapatan (Revenue)', B: 'Penjualan Makanan', C: 50000000 },
    { A: 'Pendapatan (Revenue)', B: 'Penjualan Minuman', C: 25000000 },
    { A: 'Harga Pokok Produksi', B: 'Bahan Baku Makanan', C: 15000000 },
    { A: 'Harga Pokok Produksi', B: 'Bahan Baku Minuman', C: 5000000 },
    { A: 'Beban Operasional (OPEX)', B: 'Wages', C: 12000000 },
    { A: 'Beban Operasional (OPEX)', B: 'Rent', C: 8000000 },
    { A: 'Beban Operasional (OPEX)', B: 'Utilitas (Listrik, Air)', C: 3000000 },
    { A: 'Beban Non Operasional', B: 'Advertising', C: 2000000 },
    { A: 'Beban Non Operasional', B: 'Biaya Lainnya', C: 1000000 },
  ]
  const mainCategories = ['Pendapatan (Revenue)', 'Harga Pokok Produksi', 'Beban Operasional (OPEX)', 'Beban Non Operasional', 'Depresiasi/ Amortisasi', 'Bunga', 'Pajak (PB1)']

  const wsInstructions = XLSX.utils.json_to_sheet([...instructions, {}, { Step: 'Valid Main Categories:' }, ...mainCategories.map((cat) => ({ Step: `  - ${cat}` }))], { skipHeader: true })
  const wsData = XLSX.utils.json_to_sheet(pnlSheetData, { skipHeader: true })

  wsInstructions['!cols'] = [{ wch: 25 }, { wch: 120 }]
  wsData['!cols'] = [{ wch: 30 }, { wch: 30 }, { wch: 20 }]

  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions')
  XLSX.utils.book_append_sheet(wb, wsData, 'P&L Data')
  XLSX.writeFile(wb, 'Finalytics_P&L_Template.xlsx')
}